import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "screen0", "screen1", "screen2", "screen3", "screen4",
    "backButton", "screensContainer",
    "progressBar0", "progressBar1", "progressBar2", "progressBar3", "progressBar4",
    "progressBarWrapper0", "progressBarWrapper1", "progressBarWrapper2", "progressBarWrapper3", "progressBarWrapper4",
    "totalStats", "totalWinners", "totalErrors"
  ]

  static values = {
    matchId: Number,
    recordUrl: String
  }

  connect() {
    this.currentScreen = 0
    this.mode = null // 'simple' ou 'advanced'
    this.statData = {
      player_id: null,
      stat_type: null,
      shot_type: null,
      zone: null
    }

    this.showScreen(0)
  }

  // Écran 0: Choix du mode
  selectMode(event) {
    this.mode = event.currentTarget.dataset.mode
    console.log('Mode sélectionné:', this.mode)
    this.updateProgressBarVisibility()
    this.showScreen(1)
  }

  // Afficher/cacher les barres selon le mode
  updateProgressBarVisibility() {
    const wrappers = [
      this.progressBarWrapper0Target,
      this.progressBarWrapper1Target,
      this.progressBarWrapper2Target,
      this.progressBarWrapper3Target,
      this.progressBarWrapper4Target
    ]

    if (this.mode === 'simple') {
      // Mode simple: afficher seulement 3 barres (mode + joueur + stat)
      wrappers.forEach((wrapper, index) => {
        if (index < 3) {
          wrapper.classList.remove('hidden')
        } else {
          wrapper.classList.add('hidden')
        }
      })
    } else {
      // Mode avancé: afficher toutes les barres
      wrappers.forEach(wrapper => wrapper.classList.remove('hidden'))
    }
  }

  // Écran 1: Sélection joueur
  selectPlayer(event) {
    this.statData.player_id = event.currentTarget.dataset.playerId
    this.playerName = event.currentTarget.dataset.playerName
    console.log('Joueur sélectionné:', this.playerName)
    this.showScreen(2)
  }

  // Écran 2: Winner ou Erreur
  selectStatType(event) {
    this.statData.stat_type = event.currentTarget.dataset.statType
    console.log('Type stat:', this.statData.stat_type)

    if (this.mode === 'simple') {
      // Mode simple: on envoie directement
      this.submitStat()
    } else {
      // Mode avancé: on passe au type de coup
      this.showScreen(3)
    }
  }

  // Écran 3: Type de coup (mode avancé)
  selectShotType(event) {
    this.statData.shot_type = event.currentTarget.dataset.shotType
    console.log('Type de coup:', this.statData.shot_type)

    // Auto-déduction de la zone selon le type de coup
    const autoZones = {
      'volley': 'net',        // Volée → Toujours au filet
      'bandeja': 'baseline',  // Bandeja → Toujours fond de court
      'vibora': 'baseline'    // Víbora → Toujours fond de court
    }

    if (autoZones[this.statData.shot_type]) {
      // Zone imposée : on l'assigne et on soumet directement
      this.statData.zone = autoZones[this.statData.shot_type]
      console.log('Zone auto-détectée:', this.statData.zone)
      this.submitStat()
    } else {
      // Zone variable : on demande à l'utilisateur
      this.showScreen(4)
    }
  }

  skipShotType() {
    this.statData.shot_type = null
    this.showScreen(4)
  }

  // Écran 4: Zone (mode avancé)
  selectZone(event) {
    this.statData.zone = event.currentTarget.dataset.zone
    console.log('Zone:', this.statData.zone)
    this.submitStat()
  }

  skipZone() {
    this.statData.zone = null
    this.submitStat()
  }

  // Navigation
  goBack() {
    if (this.currentScreen > 0) {
      this.showScreen(this.currentScreen - 1)
    }
  }

  showScreen(screenNumber) {
    // Cacher tous les écrans
    const screens = [
      this.screen0Target,
      this.screen1Target,
      this.screen2Target,
      this.screen3Target,
      this.screen4Target
    ]

    screens.forEach((screen, index) => {
      if (index === screenNumber) {
        screen.classList.remove('hidden')
        screen.classList.add('opacity-100')
        screen.classList.remove('opacity-0')
      } else {
        screen.classList.add('hidden')
        screen.classList.remove('opacity-100')
        screen.classList.add('opacity-0')
      }
    })

    this.currentScreen = screenNumber

    // Mettre à jour la barre de progression
    this.updateProgressBar()

    // Afficher/cacher le bouton retour
    if (screenNumber === 0) {
      this.backButtonTarget.classList.add('hidden')
    } else {
      this.backButtonTarget.classList.remove('hidden')
    }
  }

  updateProgressBar() {
    const progressBars = [
      this.progressBar0Target,
      this.progressBar1Target,
      this.progressBar2Target,
      this.progressBar3Target,
      this.progressBar4Target
    ]

    // Calculer le % de complétion en fonction du mode
    let totalSteps = this.mode === 'simple' ? 2 : 4  // Simple: joueur + stat | Avancé: joueur + stat + coup + zone
    let completedSteps = 0

    if (this.currentScreen >= 1) completedSteps++  // Joueur
    if (this.currentScreen >= 2) completedSteps++  // Winner/Erreur
    if (this.mode === 'advanced') {
      if (this.currentScreen >= 3) completedSteps++  // Type coup
      if (this.currentScreen >= 4) completedSteps++  // Zone
    }

    progressBars.forEach((bar, index) => {
      if (index < completedSteps) {
        bar.style.width = '100%'
      } else if (index === completedSteps) {
        bar.style.width = '50%'
      } else {
        bar.style.width = '0%'
      }
    })
  }

  // Soumettre la stat
  async submitStat() {
    console.log('Soumission stat:', this.statData)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Créer un objet propre avec seulement les valeurs primitives
    const cleanData = {
      player_id: this.statData.player_id,
      stat_type: this.statData.stat_type,
      shot_type: this.statData.shot_type || null,
      zone: this.statData.zone || null
    }

    console.log('Clean data:', cleanData)

    try {
      // URL fournie par la vue (les routes sont préfixées par la locale)
      const url = this.recordUrlValue || `/stats/matches/${this.matchIdValue}/record_stat`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          match_stat: cleanData
        })
      })

      const result = await response.json()

      if (result.success) {
        console.log('✅ Stat enregistrée:', result.stat)

        // Feedback visuel (flash rapide)
        this.showSuccessFeedback()

        // Mettre à jour les compteurs
        this.updateCounters(result.stat.stat_type)

        // Réinitialiser et retourner à l'écran de sélection joueur
        this.resetData()
        this.showScreen(1)
      } else {
        console.error('❌ Erreur:', result.errors)
        alert('Erreur lors de l\'enregistrement: ' + result.errors.join(', '))
      }
    } catch (error) {
      console.error('❌ Erreur réseau:', error)
      alert('Erreur de connexion. Veuillez réessayer.')
    }
  }

  resetData() {
    this.statData = {
      player_id: null,
      stat_type: null,
      shot_type: null,
      zone: null
    }
  }

  showSuccessFeedback() {
    // Flash de succès sur tout l'écran
    const flash = document.createElement('div')
    flash.className = 'fixed inset-0 bg-green-500/30 backdrop-blur-sm flex items-center justify-center z-50 pointer-events-none'
    flash.innerHTML = `
      <div class="bg-white rounded-full p-12 shadow-2xl transform scale-0 animate-ping-once">
        <svg class="w-24 h-24 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
        </svg>
      </div>
    `
    document.body.appendChild(flash)

    setTimeout(() => {
      flash.remove()
    }, 800)
  }

  updateCounters(statType) {
    // Incrémenter total
    const currentTotal = parseInt(this.totalStatsTarget.textContent)
    this.totalStatsTarget.textContent = currentTotal + 1

    // Incrémenter winner ou erreur
    if (statType === 'winner') {
      const currentWinners = parseInt(this.totalWinnersTarget.textContent)
      this.totalWinnersTarget.textContent = currentWinners + 1
    } else {
      const currentErrors = parseInt(this.totalErrorsTarget.textContent)
      this.totalErrorsTarget.textContent = currentErrors + 1
    }

    // Animation de pulse
    const target = statType === 'winner' ? this.totalWinnersTarget : this.totalErrorsTarget
    target.classList.add('scale-125')
    setTimeout(() => target.classList.remove('scale-125'), 300)
  }
}
